<script setup lang="ts">
import { useDebounceFn } from "@vueuse/core";
import { CreateStockCountEntry, SearchStockCountItems } from "@/api/stockCountApi";
import ConfirmActionDialog from "@/components/dialogs/ConfirmActionDialog.vue";
import MobilePageTitle from "@/components/shared/mobile/MobilePageTitle.vue";
import StockCountLineCard from "@/components/stock-count/StockCountLineCard.vue";
import ContinuousBarcodeScanner from "@/components/shared/ContinuousBarcodeScanner.vue";
import { useMobileShell } from "@/composables/useMobileShell";
import { useStockCountSession } from "@/composables/useStockCountSession";
import { presentStockEntryError } from "@/services/stockEntryErrors";
import useToastify from "@/stores/toastify.store";
import { signalScan } from "@/utils/scanFeedback";
import type {
  StockCountItem,
  ContinuousBarcodeScannerFeedback,
  StockCountScanOutcome,
} from "@/type/stockCount";

/**
 * Stocktaking (الجرد) — a phone-first counting surface.
 *
 * The whole flow is four steps and nothing else:
 *   1. pick the warehouse
 *   2. scan a barcode — the item is found and added to the list
 *   3. adjust the count (or scan the same label again to bump it)
 *   4. finish — one DRAFT stock entry is created and the list clears
 *
 * The page is one narrow column: camera pinned at the top, counted lines below
 * it, totals pinned at the bottom. That shape is the requirement, not a
 * responsive fallback — the operator walks the aisle holding the phone, and the
 * camera must never scroll out of reach. On a desktop the same column is centred
 * with a note saying so, which keeps the page testable without a second layout
 * to maintain.
 *
 * The draft moves no stock. Submitting it is what writes the ledger, and that is
 * left to the backend so a count is reviewed by someone who can see the whole
 * document first.
 */

definePage({
  meta: {
    // definePage meta is hoisted out of component scope at compile time, so it
    // must be a static literal — a runtime import like PAGE_KEYS would throw
    // "PAGE_KEYS is not defined". Value mirrors PAGE_KEYS.WAREHOUSE_STOCK_COUNT.
    permissionKey: "page.warehouse.stock_count",
  },
});

const { t } = useI18n();
const toast = useToastify();
const { isMobileShell } = useMobileShell();

const {
  warehouse,
  warehouseOptions,
  warehousesLoading,
  orderedLines,
  totalLines,
  totalCountedQty,
  zeroQtyLineCount,
  isReadyToScan,
  missingRequirement,
  ensureWarehouses,
  addItem,
  addByBarcode,
  setQty,
  stepQty,
  setLineWarehouse,
  removeLine,
  clearLines,
  buildSubmitPayload,
} = useStockCountSession();

/**
 * ⚠️ NO `canDoctype` gate on the actions, deliberately.
 *
 * Reaching this page is already gated by `permissionKey` above. `canDoctype`
 * reads whatever the backend access snapshot enumerates, and a custom doctype
 * that is absent from that map returns `false` — which would disable Finish for
 * exactly the stock managers the feature exists for. The backend refuses what it
 * must, and `presentStockEntryError` renders the refusal. Add a gate only after
 * confirming the access payload actually lists these doctypes.
 */

const cameraActive = ref(true);
const scanning = ref(false);
const feedback = ref<ContinuousBarcodeScannerFeedback | null>(null);
const highlightedLineId = ref<string | null>(null);

const manualQuery = ref("");
const manualResults = ref<StockCountItem[]>([]);
const manualLoading = ref(false);
const manualOpen = ref(false);

const submitting = ref(false);
const clearDialogOpen = ref(false);
const confirmSaveOpen = ref(false);
const savedEntryId = ref<string | null>(null);
/** Set once the warehouse fetch has settled, so the empty state cannot flash while loading. */
const warehousesReady = ref(false);

const blockedHint = computed(() =>
  missingRequirement() ? t("alkokh.stockCount.scanner.selectWarehouseFirst") : undefined,
);

const summaryChips = computed(() => [
  { key: "lines", label: t("alkokh.stockCount.summary.lines"), value: String(totalLines.value) },
  { key: "qty", label: t("alkokh.stockCount.summary.totalQty"), value: String(totalCountedQty.value) },
]);

const confirmSaveMessage = computed(() => {
  const base = t("alkokh.stockCount.confirm.message", {
    lines: totalLines.value,
    qty: totalCountedQty.value,
    warehouse: warehouse.value,
  });

  return zeroQtyLineCount.value
    ? `${base}\n${t("alkokh.stockCount.confirm.skippedZero", { count: zeroQtyLineCount.value })}`
    : base;
});

let feedbackTimer: ReturnType<typeof setTimeout> | null = null;

function showFeedback(next: ContinuousBarcodeScannerFeedback) {
  feedback.value = next;

  if (feedbackTimer)
    clearTimeout(feedbackTimer);

  feedbackTimer = setTimeout(() => {
    feedback.value = null;
  }, 2600);
}

let highlightTimer: ReturnType<typeof setTimeout> | null = null;

function highlight(lineId: string) {
  highlightedLineId.value = lineId;

  if (highlightTimer)
    clearTimeout(highlightTimer);

  highlightTimer = setTimeout(() => {
    highlightedLineId.value = null;
  }, 1400);
}

function reportOutcome(outcome: StockCountScanOutcome) {
  switch (outcome.status) {
    case "added":
      signalScan("ok");
      showFeedback({
        tone: "success",
        text: t("alkokh.stockCount.feedback.added", { item: outcome.line.itemName }),
      });
      highlight(outcome.line.id);
      break;

    case "incremented":
      signalScan("duplicate");
      showFeedback({
        tone: "success",
        text: t("alkokh.stockCount.feedback.incremented", {
          item: outcome.line.itemName,
          qty: outcome.line.countedQty,
        }),
      });
      highlight(outcome.line.id);
      break;

    case "not-found":
      signalScan("error");
      showFeedback({
        tone: "warning",
        text: t("alkokh.stockCount.feedback.notFound", { barcode: outcome.barcode }),
      });
      break;

    case "blocked":
      signalScan("error");
      showFeedback({ tone: "warning", text: t("alkokh.stockCount.scanner.selectWarehouseFirst") });
      break;

    case "failed":
      signalScan("error");
      showFeedback({ tone: "error", text: t("alkokh.stockCount.feedback.failed") });
      break;
  }
}

/**
 * Scans are drained one at a time.
 *
 * Two codes can land within a few hundred milliseconds — a sweep across a shelf
 * catches neighbours — and firing their lookups in parallel would put the
 * feedback banner out of order relative to the list. Serialising also keeps the
 * request rate to something a shared backend will not throttle. The cap guards
 * the pathological case of a code-dense poster filling the queue faster than it
 * drains.
 */
const SCAN_QUEUE_LIMIT = 12;
const scanQueue: string[] = [];
let draining = false;

async function drainScanQueue() {
  if (draining)
    return;

  draining = true;
  scanning.value = true;

  try {
    while (scanQueue.length) {
      const code = scanQueue.shift();
      if (!code)
        continue;

      reportOutcome(await addByBarcode(code));
    }
  }
  finally {
    draining = false;
    scanning.value = false;
  }
}

function onDecoded(code: string) {
  const missing = missingRequirement();
  if (missing) {
    reportOutcome({ status: "blocked", reason: missing });

    return;
  }

  if (scanQueue.length >= SCAN_QUEUE_LIMIT)
    return;

  scanQueue.push(code);
  void drainScanQueue();
}

function onScannerUnavailable() {
  // The camera is genuinely unavailable on the plain-HTTP deployment, so manual
  // entry is opened for the operator rather than left for them to find. With
  // no printed labels this is the primary path, not a fallback.
  manualOpen.value = true;
}

const runManualSearch = useDebounceFn(async (term: string) => {
  manualResults.value = term.trim().length >= 2
    ? await SearchStockCountItems(manualLoading, term)
    : [];
}, 300);

watch(manualQuery, (term) => {
  void runManualSearch(term);
});

/** Enter in the manual field means "this is a code", not "search for this". */
async function submitManualCode() {
  const code = manualQuery.value.trim();
  if (!code)
    return;

  reportOutcome(await addByBarcode(code));
  manualQuery.value = "";
  manualResults.value = [];
}

/** English name and group alongside the code, so a search hit is identifiable. */
function manualSubtitle(item: StockCountItem) {
  return [
    item.englishName && item.englishName !== item.itemName ? item.englishName : null,
    item.itemCode,
    item.category,
  ].filter(Boolean).join(" · ");
}

function addManualResult(item: StockCountItem) {
  reportOutcome(addItem(item));
  manualQuery.value = "";
  manualResults.value = [];
}

function confirmClear() {
  clearLines();
  clearDialogOpen.value = false;
  toast.success({ text: t("alkokh.stockCount.messages.cleared") });
}

async function saveCount() {
  confirmSaveOpen.value = false;

  try {
    const result = await CreateStockCountEntry(submitting, buildSubmitPayload());

    savedEntryId.value = result.id;
    clearLines();
    toast.success({ text: t("alkokh.stockCount.messages.saved", { id: result.id }) });
  }
  catch (error: any) {
    // The count is deliberately NOT cleared on failure: the walk down the aisle
    // is the expensive part and must survive a refusal.
    const presentation = presentStockEntryError(error, t);
    toast.error({ text: presentation.toastMessage || t("alkokh.stockCount.messages.saveFailed") });
  }
}

onMounted(async () => {
  await ensureWarehouses();
  warehousesReady.value = true;
});

onBeforeUnmount(() => {
  if (feedbackTimer)
    clearTimeout(feedbackTimer);

  if (highlightTimer)
    clearTimeout(highlightTimer);
});
</script>

<template>
  <div class="stock-count-page">
    <div class="stock-count-page__column">
      <MobilePageTitle
        :title="t('alkokh.stockCount.title')"
        :subtitle="t('alkokh.stockCount.subtitle')"
      />

      <VAlert
        v-if="!isMobileShell"
        type="info"
        variant="tonal"
        density="compact"
        class="mb-4"
      >
        {{ t("alkokh.stockCount.desktopNotice") }}
      </VAlert>

      <!-- An empty list means the query matched nothing or failed. Saying so beats
           rendering a shorter list that looks legitimate. -->
      <VAlert
        v-if="warehousesReady && !warehouseOptions.length"
        type="error"
        variant="tonal"
        density="compact"
        class="mb-4"
      >
        {{ t("alkokh.stockCount.errors.noWarehouses") }}
      </VAlert>

      <!-- Confirmation of the last save. Dismissible and non-blocking: the
           operator can start the next warehouse straight away. -->
      <VAlert
        v-if="savedEntryId"
        type="success"
        variant="tonal"
        density="compact"
        closable
        class="mb-4"
        @click:close="savedEntryId = null"
      >
        {{ t("alkokh.stockCount.messages.saved", { id: savedEntryId }) }}
      </VAlert>

      <!-- Camera + warehouse stay pinned: the operator's hands are busy and the
           scanner must not scroll away as the counted list grows. -->
      <div class="stock-count-page__sticky">
        <ContinuousBarcodeScanner
          v-model:active="cameraActive"
          :blocked="!isReadyToScan"
          :blocked-hint="blockedHint"
          :feedback="feedback"
          @decoded="onDecoded"
          @unavailable="onScannerUnavailable"
        />

        <div class="stock-count-page__warehouse">
          <AppSelect
            v-model="warehouse"
            :items="warehouseOptions"
            :label="t('alkokh.stockCount.warehouse')"
            :loading="warehousesLoading"
            density="comfortable"
            hide-details
            class="flex-grow-1"
          />

          <VBtn
            :icon="manualOpen ? 'tabler-keyboard-off' : 'tabler-keyboard'"
            variant="tonal"
            :aria-label="t('alkokh.stockCount.manual.toggle')"
            @click="manualOpen = !manualOpen"
          />
        </div>

        <VExpandTransition>
          <div
            v-if="manualOpen"
            class="stock-count-page__manual"
          >
            <AppTextField
              v-model="manualQuery"
              :label="t('alkokh.stockCount.manual.label')"
              :placeholder="t('alkokh.stockCount.manual.placeholder')"
              :loading="manualLoading"
              :disabled="!isReadyToScan"
              density="comfortable"
              clearable
              @keyup.enter="submitManualCode"
            />

            <VList
              v-if="manualResults.length"
              class="stock-count-page__manual-results"
              density="compact"
            >
              <VListItem
                v-for="item in manualResults"
                :key="item.itemCode"
                :title="item.itemName"
                :subtitle="manualSubtitle(item)"
                @click="addManualResult(item)"
              >
                <template #append>
                  <VIcon icon="tabler-plus" size="18" />
                </template>
              </VListItem>
            </VList>

            <div class="stock-count-page__manual-hint">
              {{ t("alkokh.stockCount.manual.hint") }}
            </div>
          </div>
        </VExpandTransition>

        <VProgressLinear
          v-if="scanning"
          indeterminate
          color="primary"
          height="2"
          class="mt-1"
        />
      </div>

      <div class="stock-count-page__lines">
        <div class="stock-count-page__lines-header">
          <span class="stock-count-page__lines-title">
            {{ t("alkokh.stockCount.countedItems") }}
          </span>
          <VBtn
            v-if="totalLines"
            variant="text"
            color="error"
            size="small"
            density="comfortable"
            @click="clearDialogOpen = true"
          >
            {{ t("alkokh.stockCount.actions.clear") }}
          </VBtn>
        </div>

        <div
          v-if="!totalLines"
          class="stock-count-page__empty"
        >
          <VIcon
            icon="tabler-barcode"
            size="34"
            class="mb-2"
          />
          <div>{{ t("alkokh.stockCount.empty") }}</div>
        </div>

        <StockCountLineCard
          v-for="line in orderedLines"
          :key="line.id"
          :line="line"
          :highlighted="line.id === highlightedLineId"
          @step="stepQty(line.id, $event)"
          @set-qty="setQty(line.id, $event)"
          @set-warehouse="setLineWarehouse(line.id, $event)"
          @remove="removeLine(line.id)"
        />
      </div>

      <!-- Totals bar. Sticky so the running count is always readable, and it
           carries the finish action so there is one place to end the session. -->
      <div
        v-if="totalLines"
        class="stock-count-page__footer"
      >
        <div class="stock-count-page__footer-stats">
          <div
            v-for="chip in summaryChips"
            :key="chip.key"
            class="stock-count-page__stat"
          >
            <span class="stock-count-page__stat-value">{{ chip.value }}</span>
            <span class="stock-count-page__stat-label">{{ chip.label }}</span>
          </div>
        </div>

        <VBtn
          color="primary"
          block
          :loading="submitting"
          :disabled="submitting || !isReadyToScan"
          @click="confirmSaveOpen = true"
        >
          {{ t("alkokh.stockCount.actions.finish") }}
        </VBtn>
      </div>
    </div>

    <ConfirmActionDialog
      :is-dialog-visible="confirmSaveOpen"
      :title="t('alkokh.stockCount.confirm.title')"
      :message="confirmSaveMessage"
      :confirm-btn="t('alkokh.stockCount.actions.finish')"
      icon="tabler-device-floppy"
      :loading="submitting"
      @update:is-dialog-visible="confirmSaveOpen = $event"
      @confirm="saveCount"
    />

    <ConfirmActionDialog
      :is-dialog-visible="clearDialogOpen"
      :title="t('alkokh.stockCount.clearDialog.title')"
      :message="t('alkokh.stockCount.clearDialog.message')"
      :confirm-btn="t('alkokh.stockCount.actions.clear')"
      confirm-color="error"
      icon="tabler-trash"
      @update:is-dialog-visible="clearDialogOpen = $event"
      @confirm="confirmClear"
    />
  </div>
</template>

<style lang="scss" scoped>
.stock-count-page {
  display: flex;
  justify-content: center;

  &__column {
    /* One phone-width column at every size. See the component doc comment: the
       narrow layout is the requirement, so desktop centres it rather than
       spreading into a second design. */
    inline-size: 100%;
    max-inline-size: 30rem;
  }

  &__sticky {
    position: sticky;
    /* Clears the phone shell's app bar; on desktop it clears the navbar. */
    inset-block-start: 0;
    z-index: 2;
    padding-block-end: 0.75rem;
    background: rgb(var(--v-theme-background));
  }

  &__warehouse {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-block-start: 0.75rem;
  }

  &__manual {
    margin-block-start: 0.625rem;
  }

  &__manual-results {
    margin-block-start: 0.375rem;
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    border-radius: 8px;
    max-block-size: 14rem;
    overflow-y: auto;
  }

  &__manual-hint {
    margin-block-start: 0.375rem;
    color: rgba(var(--v-theme-on-surface), 0.6);
    font-size: 0.75rem;
    line-height: 1.35;
  }

  &__lines {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding-block-start: 0.25rem;
  }

  &__lines-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
  }

  &__lines-title {
    font-size: 0.8125rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: rgba(var(--v-theme-on-surface), 0.6);
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-block: 2.5rem;
    color: rgba(var(--v-theme-on-surface), 0.5);
    font-size: 0.875rem;
    text-align: center;
  }

  &__footer {
    position: sticky;
    inset-block-end: 0;
    z-index: 2;
    margin-block-start: 1rem;
    padding-block: 0.75rem;
    background: rgb(var(--v-theme-background));
    box-shadow: 0 -6px 12px -8px rgba(var(--v-shadow-key-umbra-color), 0.4);
  }

  &__footer-stats {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    margin-block-end: 0.625rem;
  }

  &__stat {
    display: flex;
    flex: 1 1 0;
    flex-direction: column;
    align-items: center;
    min-inline-size: 0;
  }

  &__stat-value {
    font-size: 1.125rem;
    font-weight: 600;
    line-height: 1.2;
  }

  &__stat-label {
    color: rgba(var(--v-theme-on-surface), 0.6);
    font-size: 0.6875rem;
    text-align: center;
  }
}
</style>
